import { useState } from 'react';
import { color } from '../styles/theme';
import { dateTime, num } from '../lib/format';
import { useRedcapAnalytics } from '../api/redcap';
import type { RedcapFieldStats } from '../api/client';
import { Button, Card, Empty, ErrorNote, Field, Loading, selectStyle } from '../components/ui';
import { GuideCard, MONO, Note, Stat, Where } from './RedcapShared';
import type { RedcapTab } from './RedcapSteps';

/**
 * Analytics — counts over the study's imported REDCap records, read through
 * its data dictionary so a code shows as the answer it stands for.
 *
 * Only the de-identified side is counted: the record import refuses
 * identifier columns and leaves the trial record out, and nothing here joins a
 * record to a person. One series per field, so one colour; the count and share
 * sit beside every bar in text, so nothing depends on colour or on hovering.
 */

const BAR = 14;

function pct(n: number, of: number): string {
  if (of === 0) return '0%';
  const p = (n / of) * 100;
  return p > 0 && p < 1 ? '<1%' : `${Math.round(p)}%`;
}

function ChoiceBars({ s }: { s: RedcapFieldStats }) {
  const bars = [
    ...(s.choices ?? []).map((c) => ({ key: c.code, label: c.label, code: c.code, n: c.n, other: false })),
    ...(s.other > 0 ? [{ key: '__other', label: 'Not in the dictionary', code: '', n: s.other, other: true }] : []),
  ];
  const max = Math.max(1, ...bars.map((b) => b.n));
  // A checkbox can hold several answers per record, so its shares are of the
  // records that answered, and need not add up to 100.
  const of = s.answered;

  return (
    <div role="table" aria-label={s.label} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {bars.map((b) => (
        <div
          key={b.key}
          role="row"
          title={`${b.label}: ${num(b.n)} (${pct(b.n, of)})`}
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1.1fr) 92px',
            gap: 10,
            alignItems: 'center',
            fontSize: 12.5,
          }}
        >
          <span
            role="cell"
            // A long answer ("Native American/American Indian/…") wraps inside its own column.
            style={{ color: b.other ? color.muted : color.ink, lineHeight: '16px', overflowWrap: 'anywhere' }}
          >
            {b.label}
            {!b.other && s.type !== 'yesno' && s.type !== 'truefalse' && (
              <span style={{ color: color.muted, fontFamily: MONO, fontSize: 11 }}> · {b.code}</span>
            )}
          </span>
          <span aria-hidden="true" style={{ display: 'flex', alignItems: 'center', height: BAR + 4 }}>
            <span
              style={{
                display: 'block',
                height: BAR,
                width: b.n === 0 ? 0 : `max(2px, ${(b.n / max) * 100}%)`,
                background: b.other ? color.muted : color.blue,
                // Square where it grows from, rounded at the end that carries the value.
                borderRadius: '0 4px 4px 0',
              }}
            />
          </span>
          <span
            role="cell"
            style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: color.ink, fontWeight: 700 }}
          >
            {num(b.n)} <span style={{ color: color.muted, fontWeight: 600 }}>({pct(b.n, of)})</span>
          </span>
        </div>
      ))}
    </div>
  );
}

function FieldCard({ s }: { s: RedcapFieldStats }) {
  return (
    <Card pad="16px 18px">
      <p style={{ margin: 0, fontSize: 14, fontWeight: 800, color: color.navy, lineHeight: '19px' }}>{s.label}</p>
      <p style={{ margin: '3px 0 12px', fontSize: 11.5, color: color.muted }}>
        <code style={{ fontFamily: MONO }}>{s.variable}</code> · {s.form} · {num(s.answered)}{' '}
        {s.answered === 1 ? 'answer' : 'answers'}
      </p>
      {s.choices && <ChoiceBars s={s} />}
      {s.numeric && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 8 }}>
          <Stat label="Lowest" value={num(s.numeric.min)} />
          <Stat label="Median" value={num(s.numeric.median)} />
          <Stat label="Mean" value={num(s.numeric.mean)} />
          <Stat label="Highest" value={num(s.numeric.max)} />
        </div>
      )}
      {!s.choices && !s.numeric && <Note>No numeric answers.</Note>}
    </Card>
  );
}

export function RedcapAnalytics({ slug, onGo }: { slug: string; onGo: (tab: RedcapTab) => void }) {
  const [event, setEvent] = useState<string | null>(null);
  const [form, setForm] = useState<string | null>(null);
  // Read without a form, so the form list is the event's and never shrinks to the one picked.
  const base = useRedcapAnalytics(slug, { event, form: null });
  const shown = useRedcapAnalytics(slug, { event, form });

  if (base.isLoading) return <Loading />;
  if (base.error) return <ErrorNote message={(base.error as Error).message} />;
  const a = base.data!;

  if (a.events.length === 0) {
    return (
      <Card>
        <Empty>
          <span style={{ display: 'block' }}>
            Nothing to count yet. Analytics reads the records brought back in step 4 — import a de-identified export
            under Record import and its answers are counted here.
          </span>
          <span style={{ display: 'block', marginTop: 12 }}>
            <Button variant="soft" onClick={() => onGo('records')}>
              Go to Record import
            </Button>
          </span>
        </Empty>
      </Card>
    );
  }

  const data = form ? shown.data : a;
  const longitudinal = a.events.length > 1;
  const fetching = base.isFetching || (form !== null && shown.isFetching);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <GuideCard
        storageKey="enact.redcap.guide.analytics"
        intro="Counts over the de-identified answers brought back in step 4, read through the dictionary so a code shows as the answer it stands for. Nothing here joins a record to a person."
        steps={[
          {
            do: 'Pick a visit, then a form.',
            where: (
              <Where at="here">
                an event is a visit. With every event counted, a question asked at several visits counts each
                visit’s answer; pick one event to see one visit.
              </Where>
            ),
          },
          {
            do: 'Read the bars.',
            where: (
              <Where at="here">
                the count and share sit beside every bar. Shares are of the rows that answered; a checkbox allows
                several answers, so its shares need not add to 100%.
              </Where>
            ),
          },
        ]}
      />
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        {longitudinal && (
          <Field label="Event" style={{ width: 260, maxWidth: '100%' }}>
            <select
              value={event ?? ''}
              onChange={(e) => {
                setEvent(e.target.value || null);
                // A form picked at one event may not be given at the next.
                setForm(null);
              }}
              style={selectStyle}
            >
              <option value="">Every event ({num(a.events.reduce((n, e) => n + e.rows, 0))} rows)</option>
              {a.events.map((e) => (
                <option key={e.name} value={e.name}>
                  {e.name || 'No event'} ({num(e.rows)})
                </option>
              ))}
            </select>
          </Field>
        )}
        <Field label="Form" style={{ width: 300, maxWidth: '100%' }}>
          <select value={form ?? ''} onChange={(e) => setForm(e.target.value || null)} style={selectStyle}>
            <option value="">Every form ({a.forms.reduce((n, f) => n + f.fields, 0)} fields)</option>
            {a.forms.map((f) => (
              <option key={f.name} value={f.name}>
                {f.name} ({f.fields})
              </option>
            ))}
          </select>
        </Field>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(120px, 1fr))', gap: 8, flex: 1, minWidth: 320 }}>
          <Stat label="Records" value={num(a.records)} />
          <Stat label={event ? 'Rows at this event' : longitudinal ? 'Rows (records × events)' : 'Rows'} value={num(a.rows)} />
          <Stat label="Last import" value={a.lastImportedAt ? dateTime(a.lastImportedAt) : '—'} />
        </div>
      </div>

      {!a.dictionary && (
        <Card>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <Note>
              Upload the REDCap data dictionary under Project setup: without it the records cannot be read as
              questions and answers.
            </Note>
            <Button variant="soft" onClick={() => onGo('setup')} style={{ padding: '7px 14px', fontSize: 12.5 }}>
              Go to Project setup
            </Button>
          </div>
        </Card>
      )}

      {shown.error && form && <ErrorNote message={(shown.error as Error).message} />}
      {data && data.fields.length === 0 && a.dictionary && (
        <Card>
          <Note>
            {event
              ? 'Nothing at this event matches a dictionary field that can be counted — choices, checkboxes and numbers.'
              : 'None of this study’s imported columns match a dictionary field that can be counted — choices, checkboxes and numbers.'}
          </Note>
        </Card>
      )}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))',
          gap: 14,
          // Refetching keeps the frame rather than flashing a spinner.
          opacity: fetching ? 0.6 : 1,
          transition: 'opacity 0.15s',
        }}
      >
        {(data?.fields ?? []).map((s) => (
          <FieldCard key={s.variable} s={s} />
        ))}
      </div>
      {data && data.fields.length > 0 && (
        <Note>
          Shares are of the rows that answered each question.
          {longitudinal && !event
            ? ' With every event counted, a question asked at several visits counts each visit’s answer — pick an event to see one visit.'
            : ''}{' '}
          A checkbox allows several answers, so its shares need not add to 100%. “Not in the dictionary” counts
          answers that are none of the field’s codes or labels.
        </Note>
      )}
    </div>
  );
}
